import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save, Loader2, User, MapPin, Package, Users, CreditCard, Receipt, Ticket, Settings as Cog,
  Search, Plus, Trash2, Check, X, Pencil, Network, FileText, Paperclip,
} from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'
import { NumberInput } from '@/components/ui/NumberInput'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Badge } from '@/components/ui/Badge'
import { Toggle } from '@/components/ui/Toggle'
import { FileUpload } from '@/components/ui/FileUpload'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Sheet } from '@/components/ui/Sheet'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { CommissionInfoPanel } from '@/components/commercial/CommissionInfoPanel'
import { DocumentsManager } from '@/components/DocumentsManager'
import { standardDocuments } from '@/services/modules/DocumentsService'
import { useZodForm } from '@/hooks/useZodForm'
import { normalizeForMatch, isValidCpf } from '@/lib/validators'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { SubscribersService, type Subscriber, type SubscriberManagerGroup } from '@/services/modules/SubscribersService'
import { PlansService, type Plan } from '@/services/modules/PlansService'
import { ProductsService } from '@/services/modules/ProductsService'
import { DiscountCouponsService, type DiscountCoupon } from '@/services/modules/DiscountCouponsService'
import { LookupService, type State, type City, type DegreeRelation, type MaritalStatus } from '@/services/modules/LookupService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

// ============================================================================
// Masks & helpers
// ============================================================================
const moneyBR = (v: number) => 'R$ ' + (v ?? 0).toFixed(2).replace('.', ',')
const onlyDigits = (v: string) => (v ?? '').replace(/\D+/g, '')
const maskCpf = (v: string) => onlyDigits(v).slice(0, 11)
  .replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2')
const maskCep = (v: string) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
function maskPhone(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}
const maskCard = (v: string) => onlyDigits(v).slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ')
const maskExpiry = (v: string) => onlyDigits(v).slice(0, 4).replace(/^(\d{2})(\d)/, '$1/$2')

const CPF_RE   = /^\d{11}$/
const PHONE_RE = /^\d{10,11}$/
const CEP_RE   = /^\d{5}-?\d{3}$/

const GENDERS = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMININO',  label: 'Feminino' },
  { value: 'OUTRO',     label: 'Outro' },
]

// ============================================================================
// Schema (titular). Cartão, dependentes e cupom ficam em estado separado.
// ============================================================================
const schema = z.object({
  // Dados pessoais
  name:        z.string().trim().min(1, 'Informe o nome completo.').max(200),
  socialName:  z.string().trim().max(200).or(z.literal('')).nullable(),
  rnm:         z.string().trim().max(30).or(z.literal('')).nullable(),
  cpf:         z.string().trim().or(z.literal('')).nullable(),
  rg:          z.string().trim().max(30).or(z.literal('')).nullable(),
  birthDate:   z.string().nullable(),
  phone:       z.string().trim().or(z.literal('')).nullable(),
  mobile:      z.string().trim().or(z.literal('')).nullable(),
  businessPhone: z.string().trim().or(z.literal('')).nullable(),
  email:       z.string().trim().email('E-mail inválido.').or(z.literal('')).nullable(),
  gender:      z.enum(['MASCULINO', 'FEMININO', 'OUTRO']),
  maritalStatusId: z.string().nullable(),

  // Localização
  postalCode: z.string().trim().regex(CEP_RE, 'CEP deve ter 8 dígitos.').or(z.literal('')).nullable(),
  address:    z.string().trim().max(200).or(z.literal('')).nullable(),
  number:     z.string().trim().max(20).or(z.literal('')).nullable(),
  complement: z.string().trim().max(100).or(z.literal('')).nullable(),
  district:   z.string().trim().max(100).or(z.literal('')).nullable(),
  stateId:    z.string().nullable(),
  cityId:     z.string().nullable(),

  // Plano
  planId: z.string().min(1, 'Selecione o plano.'),

  // Faturamento
  dueDay:        z.number({ message: 'Informe o dia de vencimento.' }).int().min(1, 'Entre 1 e 28.').max(28, 'Entre 1 e 28.'),
  paymentMethod: z.enum(['BOLETO', 'CARTÃO DE CRÉDITO']),
  status:        z.enum(['ATIVO', 'INATIVO']),

  observations:  z.string().nullable(),

  // Workflow
  billingByMail: z.boolean(),
  seasonality:   z.enum(['SEMESTRAL', 'ANUAL']).nullable(),
  managerId:     z.string().nullable(),
}).superRefine((val, ctx) => {
  // CPF obrigatório (titular).
  if (!val.cpf || val.cpf.trim() === '') {
    ctx.addIssue({ code: 'custom', path: ['cpf'], message: 'Informe o CPF.' })
  } else {
    const d = onlyDigits(val.cpf)
    if (!CPF_RE.test(d)) ctx.addIssue({ code: 'custom', path: ['cpf'], message: 'CPF deve ter 11 dígitos.' })
    else if (!isValidCpf(d)) ctx.addIssue({ code: 'custom', path: ['cpf'], message: 'CPF inválido.' })
  }
  // Data de nascimento obrigatória (titular).
  if (!val.birthDate || val.birthDate.trim() === '') {
    ctx.addIssue({ code: 'custom', path: ['birthDate'], message: 'Informe a data de nascimento.' })
  }
  for (const f of ['phone', 'mobile', 'businessPhone'] as const) {
    const v = val[f]
    if (v && v !== '' && !PHONE_RE.test(onlyDigits(v))) {
      ctx.addIssue({ code: 'custom', path: [f], message: 'Telefone deve ter 10 ou 11 dígitos.' })
    }
  }
  if (val.billingByMail && !val.seasonality) {
    ctx.addIssue({ code: 'custom', path: ['seasonality'], message: 'Informe a periodicidade de envio.' })
  }
})
type FormValues = z.infer<typeof schema>

// Tipo de uma linha de dependente gerida localmente.
interface DepRow {
  id?: string
  name: string
  cpf: string
  rg: string
  birthDate: string
  gender: 'MASCULINO' | 'FEMININO' | 'OUTRO'
  maritalStatusId: string
  degreeRelationshipId: string
  /** Número da carteirinha (só leitura, vem do backend no modo edição). */
  membershipCardNumber?: string | null
  /**
   * Situação do dependente (só leitura). A lista traz também os inativos — e
   * precisa trazer: quem some da lista é EXCLUÍDO ao salvar o titular.
   */
  status?: 'ATIVO' | 'INATIVO'
}
interface CardState { holderName: string; number: string; expiry: string; cvv: string }

export function SubscriberFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const { can } = useAuthority()
  const canEdit = can('commercial.subscribers.edit')
  // Dependentes salvam junto com o titular (POST/PUT subscribers) → mesma permissão do Salvar.
  const canSave = isEdit ? canEdit : can('commercial.subscribers.create')
  const canContract = can('commercial.subscribers.contract')
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')

  const [plans, setPlans]   = useState<Plan[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([])
  const [degrees, setDegrees] = useState<DegreeRelation[]>([])
  const [managers, setManagers] = useState<SubscriberManagerGroup[]>([])
  const [cepLoading, setCepLoading] = useState(false)

  // Estado local: dependentes, cartão, cupom
  const [dependents, setDependents] = useState<DepRow[]>([])
  const [card, setCard] = useState<CardState>({ holderName: '', number: '', expiry: '', cvv: '' })
  const [coupon, setCoupon] = useState<DiscountCoupon | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)
  // Graus permitidos pelo plano (via produto). null = ainda não resolvido.
  const [allowedDegrees, setAllowedDegrees] = useState<string[] | null>(null)
  const [linkedCompany, setLinkedCompany] = useState(false)

  const form = useZodForm(schema, {
    defaultValues: {
      name: '', socialName: null, rnm: null, cpf: null, rg: null, birthDate: null,
      phone: null, mobile: null, businessPhone: null, email: null,
      gender: 'MASCULINO', maritalStatusId: null,
      postalCode: null, address: null, number: null, complement: null, district: null,
      stateId: null, cityId: null,
      planId: '',
      dueDay: 5, paymentMethod: 'BOLETO', status: 'ATIVO',
      observations: null,
      billingByMail: false, seasonality: null, managerId: null,
    },
  })
  const errs = form.formState.errors
  const stateId       = form.watch('stateId')
  const planId        = form.watch('planId')
  const paymentMethod = form.watch('paymentMethod')
  const billingByMail = form.watch('billingByMail')
  // Vinculado a empresa: faturamento é da empresa → esconde a seção.
  const hideBilling = isEdit && linkedCompany
  // Titular individual em edição: forma de pagamento é trocada pela listagem.
  const lockPaymentMethod = isEdit && !linkedCompany

  // Catálogos
  useEffect(() => {
    PlansService.list({ size: 100, status: 'ATIVO' }).then(r => setPlans(r.data)).catch(() => setPlans([]))
    LookupService.states().then(setStates).catch(() => setStates([]))
    LookupService.maritalStatuses().then(setMaritalStatuses).catch(() => setMaritalStatuses([]))
    LookupService.degreeRelationships(100).then(setDegrees).catch(() => setDegrees([]))
    SubscribersService.managerOptions().then(setManagers).catch(() => setManagers([]))
  }, [])

  // Cidades por estado
  useEffect(() => {
    if (!stateId) { setCities([]); return }
    LookupService.cities(stateId).then(setCities).catch(() => setCities([]))
  }, [stateId])

  // Quando o plano muda, resolve os graus de parentesco permitidos (via produto).
  useEffect(() => {
    if (!planId) { setAllowedDegrees(null); return }
    let active = true
    ;(async () => {
      try {
        const plan = await PlansService.show(planId)
        if (!plan.productId) { if (active) setAllowedDegrees([]); return }
        const product = await ProductsService.show(plan.productId)
        const modality = (product.modalities ?? []).find(
          m => m.planTypeRelationshipId === plan.planTypeRelationshipId,
        )
        // graus permitidos = todos da modalidade exceto TITULAR (code '00')
        const ids = (modality?.degrees ?? [])
          .filter(d => d.degree?.code !== '00' && normalizeForMatch(d.degree?.degreeRelationship) !== 'titular')
          .map(d => d.degreeRelationshipId)
        if (active) setAllowedDegrees(ids)
      } catch {
        if (active) setAllowedDegrees([])
      }
    })()
    return () => { active = false }
  }, [planId])

  // Carrega assinante em edição
  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    SubscribersService.show(id)
      .then((s: Subscriber) => {
        setLinkedCompany(!!(s.companyId ?? s.company?.id))
        const cityRel = s.address?.city
        form.reset({
          name: s.name,
          socialName: s.socialName,
          rnm: s.rnm,
          cpf: s.cpf ? maskCpf(s.cpf) : null,
          rg: s.rg,
          birthDate: s.birthDate,
          phone: s.phone ? maskPhone(s.phone) : null,
          mobile: s.mobile ? maskPhone(s.mobile) : null,
          businessPhone: s.businessPhone ? maskPhone(s.businessPhone) : null,
          email: s.email,
          gender: (s.gender as FormValues['gender']) ?? 'MASCULINO',
          maritalStatusId: s.maritalStatusId,
          postalCode: s.address?.postalCode ? maskCep(s.address.postalCode) : null,
          address: s.address?.address ?? null,
          number: s.address?.number ?? null,
          complement: s.address?.complement ?? null,
          district: s.address?.district ?? null,
          stateId: cityRel?.state?.id ?? cityRel?.stateId ?? null,
          cityId: cityRel?.id ?? null,
          planId: s.planId ?? '',
          dueDay: s.dueDay ?? 5,
          paymentMethod: (s.paymentMethod === 'CARTÃO DE CRÉDITO' ? 'CARTÃO DE CRÉDITO' : 'BOLETO'),
          status: s.status,
          observations: s.observations ?? null,
          billingByMail: s.billingByMail?.yesOrNo === 'SIM',
          seasonality: (s.billingByMail?.seasonality === 'SEMESTRAL' || s.billingByMail?.seasonality === 'ANUAL')
            ? s.billingByMail.seasonality : null,
          managerId: s.managerId,
        })
        setDependents((s.dependents ?? []).map(d => ({
          id: d.id,
          name: d.name,
          cpf: d.cpf ? maskCpf(d.cpf) : '',
          rg: d.rg ?? '',
          birthDate: d.birthDate ?? '',
          gender: (d.gender as DepRow['gender']) ?? 'MASCULINO',
          maritalStatusId: d.maritalStatusId ?? '',
          degreeRelationshipId: d.degreeRelationshipId ?? '',
          membershipCardNumber: d.formatted?.membershipCardNumber || d.membershipCardNumber || null,
          status: d.status === 'INATIVO' ? 'INATIVO' : 'ATIVO',
        })))
        if (s.discountCoupon) {
          setCoupon(s.discountCoupon as DiscountCoupon)
          setCouponCode(s.discountCoupon.code)
        }
      })
      .catch(() => toast.error({ title: 'Não encontrado', description: 'Assinante não localizado.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  async function lookupCep() {
    const cleaned = onlyDigits(form.getValues('postalCode') ?? '')
    if (cleaned.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const data = await SalesLinkService.lookupCep(cleaned)
      if (!data) { toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' }); return }
      form.setValue('address', data.address, { shouldValidate: false })
      form.setValue('district', data.district, { shouldValidate: false })
      const st = states.find(s => s.abbreviation === data.stateAbbr)
      if (!st) { toast.warning({ title: 'Estado não localizado', description: 'Preencha manualmente.' }); return }
      form.setValue('stateId', st.id, { shouldValidate: false })
      let cs = await LookupService.cities(st.id)
      const target = normalizeForMatch(data.cityName)
      let ci = cs.find(c => normalizeForMatch(c.city) === target)
      if (!ci) {
        try {
          const created = await LookupService.findOrCreateCity(data.cityName, st.abbreviation)
          ci = created
          cs = [...cs, created].sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
        } catch { /* manual */ }
      }
      setCities(cs)
      if (ci) form.setValue('cityId', ci.id, { shouldValidate: false })
    } catch {
      toast.error({ title: 'Falha ao buscar CEP', description: 'Verifique sua conexão e tente novamente.' })
    } finally { setCepLoading(false) }
  }

  async function applyCoupon() {
    if (!planId) { toast.error({ title: 'Selecione o plano', description: 'O cupom é validado para o plano escolhido.' }); return }
    if (!couponCode.trim()) return
    setCouponLoading(true)
    try {
      // Comparação no backend é case-insensitive — envia como digitado.
      const c = await DiscountCouponsService.findByCode(couponCode.trim(), planId)
      if (!c) { toast.error({ title: 'Cupom inválido', description: 'Cupom inexistente, expirado ou não aplicável a este plano.' }); return }
      setCoupon(c)
      const b = couponBreakdownFor(c)
      toast.success({
        title: 'Cupom aplicado',
        description: b
          ? `${c.code}: plano ${moneyBR(b.original)} − desconto ${b.discountLabel} = ${moneyBR(b.final)}`
          : `${c.code} — ${c.formatted?.value ?? ''}`,
      })
    } finally { setCouponLoading(false) }
  }
  function removeCoupon() { setCoupon(null); setCouponCode('') }

  /** Detalhe do desconto do cupom sobre o plano selecionado. */
  function couponBreakdownFor(c: DiscountCoupon | null) {
    if (!c) return null
    const original = plans.find(p => p.id === planId)?.price ?? 0
    const discount = c.discountType === 'PORCENTAGEM'
      ? Math.min(original * (c.value / 100), original)
      : Math.min(c.value, original)
    const final = Math.max(0, original - discount)
    const discountLabel = c.discountType === 'PORCENTAGEM'
      ? `${c.value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% (${moneyBR(discount)})`
      : moneyBR(c.value)
    return { original, discount, final, discountLabel }
  }

  // Trocar de plano invalida o cupom aplicado (a validade é por plano).
  const prevPlanRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prev = prevPlanRef.current
    prevPlanRef.current = planId
    // `prev` truthy = já havia um plano real selecionado → mudança feita pelo
    // usuário. Ignora a transição inicial ('' → plano do prefill na edição),
    // que não deve remover o cupom já aplicado.
    if (prev && prev !== planId && coupon) {
      setCoupon(null)
      setCouponCode('')
      toast.warning({ title: 'Cupom removido', description: 'Você alterou o plano. Aplique o cupom novamente para validá-lo para o novo plano.' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId])

  const planAllowsDependents = (allowedDegrees?.length ?? 0) > 0

  async function onSubmit(values: FormValues) {
    if (!canSave) return
    // Validação leve do cartão (apenas no cadastro com cartão)
    if (!isEdit && values.paymentMethod === 'CARTÃO DE CRÉDITO') {
      if (onlyDigits(card.number).length < 13 || !card.holderName.trim() || onlyDigits(card.expiry).length !== 4 || onlyDigits(card.cvv).length < 3) {
        toast.error({ title: 'Dados do cartão incompletos', description: 'Preencha nome, número, validade (MM/AA) e CVV.' })
        setActiveSection('card')
        return
      }
    }
    try {
      const payload: Record<string, unknown> = {
        name: values.name,
        social_name: values.socialName || null,
        rnm: values.rnm || null,
        cpf: values.cpf ? onlyDigits(values.cpf) : null,
        rg: values.rg || null,
        birth_date: values.birthDate || null,
        phone: values.phone ? onlyDigits(values.phone) : null,
        mobile: values.mobile ? onlyDigits(values.mobile) : null,
        business_phone: values.businessPhone ? onlyDigits(values.businessPhone) : null,
        email: values.email || null,
        gender: values.gender,
        marital_status_id: values.maritalStatusId || null,
        postal_code: values.postalCode ? onlyDigits(values.postalCode) : null,
        address: values.address || null,
        number: values.number || null,
        complement: values.complement || null,
        district: values.district || null,
        city_id: values.cityId || null,
        plan_id: values.planId || null,
        due_day: values.dueDay,
        payment_method: values.paymentMethod,
        status: values.status,
        observations: values.observations || null,
        discount_coupon_id: coupon?.id ?? null,
        manager_id: values.managerId || null,
        billing_by_mail: {
          yes_or_no: values.billingByMail ? 'SIM' : 'NÃO',
          seasonality: values.billingByMail ? values.seasonality : null,
        },
        dependents: dependents.map(d => ({
          id: d.id,
          name: d.name,
          cpf: d.cpf ? onlyDigits(d.cpf) : null,
          rg: d.rg || null,
          birth_date: d.birthDate || null,
          gender: d.gender,
          marital_status_id: d.maritalStatusId || null,
          degree_relationship_id: d.degreeRelationshipId || null,
        })),
      }
      // Cartão: enviado APENAS no cadastro; nunca persistido (vai pro Asaas).
      if (!isEdit && values.paymentMethod === 'CARTÃO DE CRÉDITO') {
        payload.credit_card = {
          holder_name: card.holderName,
          number: onlyDigits(card.number),
          expiry: card.expiry,
          cvv: card.cvv,
        }
      }

      if (isEdit && id) await SubscribersService.update(id, payload as Partial<Subscriber>)
      else              await SubscribersService.create(payload as Partial<Subscriber>)

      toast.success({
        title: isEdit ? 'Assinante atualizado' : 'Assinante criado',
        description: isEdit ? 'As alterações foram salvas.' : 'O novo assinante foi cadastrado.',
      })
      navigate('/commercial/subscribers')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        social_name: 'socialName', birth_date: 'birthDate', business_phone: 'businessPhone',
        marital_status_id: 'maritalStatusId', postal_code: 'postalCode', city_id: 'cityId',
        plan_id: 'planId', due_day: 'dueDay', payment_method: 'paymentMethod', manager_id: 'managerId',
      })
    }
  }

  // Erros por seção (badges no sidemenu)
  const basicErr  = !!(errs.name || errs.cpf || errs.email || errs.phone || errs.mobile || errs.businessPhone)
  const addrErr   = !!(errs.postalCode || errs.address || errs.number || errs.district || errs.cityId)
  const planErr   = !!errs.planId
  const billErr   = !!(errs.dueDay || errs.paymentMethod)
  const wfErr     = !!errs.seasonality

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  // ===================== Sections =====================
  const basicSection = (
    <SectionShell title="Dados pessoais" description="Identificação e contato do titular.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="Nome completo" required wrapperClassName="md:col-span-2" {...form.register('name')} error={errs.name?.message} />
        <Input label="Nome social" {...form.register('socialName')} error={errs.socialName?.message} />
        <Controller name="cpf" control={form.control} render={({ field, fieldState }) => (
          <Input label="CPF" required value={field.value ?? ''} onChange={(e) => field.onChange(maskCpf(e.target.value))} placeholder="000.000.000-00" error={fieldState.error?.message} />
        )} />
        <Input label="RG" {...form.register('rg')} error={errs.rg?.message} />
        <Input label="RNM" {...form.register('rnm')} error={errs.rnm?.message} hint="Registro Nacional Migratório (estrangeiros)." />
        <Input label="Data de nascimento" type="date" required {...form.register('birthDate')} error={errs.birthDate?.message} />
        <Controller name="gender" control={form.control} render={({ field, fieldState }) => (
          <Select label="Gênero" required value={field.value} onChange={(e) => field.onChange(e.target.value)} options={GENDERS} searchable={false} clearable={false} error={fieldState.error?.message} />
        )} />
        <Controller name="maritalStatusId" control={form.control} render={({ field, fieldState }) => (
          <Select label="Estado civil" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} placeholder="Selecione"
            options={maritalStatuses.map(m => ({ value: m.id, label: m.description }))} error={fieldState.error?.message} />
        )} />

        <Controller name="phone" control={form.control} render={({ field, fieldState }) => (
          <Input label="Telefone residencial" value={field.value ?? ''} onChange={(e) => field.onChange(maskPhone(e.target.value))} placeholder="(11) 3333-4444" error={fieldState.error?.message} />
        )} />
        <Controller name="mobile" control={form.control} render={({ field, fieldState }) => (
          <Input label="Celular / WhatsApp" value={field.value ?? ''} onChange={(e) => field.onChange(maskPhone(e.target.value))} placeholder="(11) 99999-9999" error={fieldState.error?.message} />
        )} />
        <Controller name="businessPhone" control={form.control} render={({ field, fieldState }) => (
          <Input label="Telefone comercial" value={field.value ?? ''} onChange={(e) => field.onChange(maskPhone(e.target.value))} placeholder="(11) 3333-4444" error={fieldState.error?.message} />
        )} />
        <Input label="E-mail" type="email" wrapperClassName="md:col-span-3" {...form.register('email')} error={errs.email?.message} />
      </div>
    </SectionShell>
  )

  const locationSection = (
    <SectionShell title="Localização" description="Digite o CEP e clique na lupa para preencher automaticamente.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="postalCode" control={form.control} render={({ field, fieldState }) => (
          <Input label="CEP" value={field.value ?? ''} onChange={(e) => field.onChange(maskCep(e.target.value))}
            placeholder="00000-000" error={fieldState.error?.message} wrapperClassName="md:col-span-1"
            actionRight={(
              <button type="button" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP" title="Buscar CEP"
                className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </button>
            )} />
        )} />
        <Input label="Logradouro" wrapperClassName="md:col-span-2" {...form.register('address')} error={errs.address?.message} />
        <Input label="Número" {...form.register('number')} error={errs.number?.message} />
        <Input label="Complemento" {...form.register('complement')} error={errs.complement?.message} placeholder="Apto, bloco" />
        <Input label="Bairro" {...form.register('district')} error={errs.district?.message} />
        <Controller name="stateId" control={form.control} render={({ field, fieldState }) => (
          <Select label="Estado" value={field.value ?? ''} onChange={(e) => { field.onChange(e.target.value || null); form.setValue('cityId', null, { shouldValidate: false }) }}
            placeholder="UF" options={states.map(s => ({ value: s.id, label: s.abbreviation }))} error={fieldState.error?.message} />
        )} />
        <Controller name="cityId" control={form.control} render={({ field, fieldState }) => (
          <Select label="Cidade" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}
            placeholder={stateId ? 'Cidade' : 'Selecione o estado primeiro'} disabled={!stateId}
            options={cities.map(c => ({ value: c.id, label: c.city }))} error={fieldState.error?.message} wrapperClassName="md:col-span-2" />
        )} />
      </div>
    </SectionShell>
  )

  const planSection = (
    <SectionShell title="Plano" description="Plano contratado pelo titular.">
      <Controller name="planId" control={form.control} render={({ field, fieldState }) => (
        <Select label="Plano" required value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || '')}
          placeholder="Selecione o plano"
          options={plans.map(p => ({ value: p.id, label: `${p.name} — ${p.formatted?.price ?? `R$ ${p.price.toFixed(2).replace('.', ',')}`}` }))}
          error={fieldState.error?.message} wrapperClassName="md:max-w-lg" />
      )} />
    </SectionShell>
  )

  const dependentsSection = (
    <DependentsManager
      dependents={dependents}
      onChange={setDependents}
      degrees={degrees}
      allowedDegreeIds={allowedDegrees ?? []}
      maritalStatuses={maritalStatuses}
      readOnly={!canSave}
    />
  )

  const billingSection = (
    <SectionShell title="Faturamento" description="Vencimento e forma de pagamento.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller name="dueDay" control={form.control} render={({ field, fieldState }) => (
          <NumberInput label="Dia de vencimento da fatura" required value={field.value ?? null} onChange={field.onChange} min={1} max={28} error={fieldState.error?.message} hint="Entre 1 e 28." />
        )} />
        <Controller name="paymentMethod" control={form.control} render={({ field, fieldState }) => (
          <Select label="Forma de pagamento" required value={field.value} onChange={(e) => field.onChange(e.target.value)}
            options={[
              { value: 'BOLETO',            label: 'Boleto' },
              { value: 'CARTÃO DE CRÉDITO', label: 'Cartão de crédito' },
            ]}
            searchable={false} clearable={false} disabled={lockPaymentMethod} error={fieldState.error?.message}
            hint={lockPaymentMethod ? 'Para alterar, use "Mudar para boleto/cartão" na listagem de associados.' : undefined} />
        )} />
        <Controller name="status" control={form.control} render={({ field }) => (
          <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
            <StatusToggle value={field.value} onChange={field.onChange} />
          </div>
        )} />
      </div>
    </SectionShell>
  )

  const cardSection = (
    <SectionShell
      title="Dados do cartão"
      description="Usados apenas para criar a assinatura no Asaas. Por segurança (PCI), os dados do cartão NÃO são armazenados no sistema."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Nome impresso no cartão" required wrapperClassName="md:col-span-2"
          value={card.holderName} onChange={(e) => setCard(c => ({ ...c, holderName: e.target.value.toUpperCase() }))} placeholder="COMO ESTÁ NO CARTÃO" />
        <Input label="Número do cartão" required wrapperClassName="md:col-span-2"
          value={card.number} onChange={(e) => setCard(c => ({ ...c, number: maskCard(e.target.value) }))} placeholder="0000 0000 0000 0000" />
        <Input label="Validade (MM/AA)" required value={card.expiry} onChange={(e) => setCard(c => ({ ...c, expiry: maskExpiry(e.target.value) }))} placeholder="MM/AA" />
        <Input label="CVV" required value={card.cvv} onChange={(e) => setCard(c => ({ ...c, cvv: onlyDigits(e.target.value).slice(0, 4) }))} placeholder="123" />
      </div>
    </SectionShell>
  )

  const couponSection = (
    <SectionShell title="Cupom de desconto" description="Aplique um código de cupom válido para o plano selecionado.">
      {coupon ? (
        (() => {
          const b = couponBreakdownFor(coupon)
          return (
            <div className="flex items-start justify-between gap-3 rounded-md ring-1 ring-emerald-200 bg-emerald-50 px-4 py-3">
              <div className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-800">{coupon.code}</div>
                  {b ? (
                    <dl className="mt-1 space-y-0.5 text-xs text-emerald-700">
                      <div className="flex gap-2"><dt className="text-emerald-600">Valor do plano:</dt><dd className="font-medium">{moneyBR(b.original)}</dd></div>
                      <div className="flex gap-2"><dt className="text-emerald-600">Desconto:</dt><dd className="font-medium">{b.discountLabel}</dd></div>
                      <div className="flex gap-2"><dt className="text-emerald-600">Valor final:</dt><dd className="font-semibold">{moneyBR(b.final)}</dd></div>
                    </dl>
                  ) : (
                    <div className="text-xs text-emerald-700">desconto de {coupon.formatted?.value ?? ''}</div>
                  )}
                </div>
              </div>
              <button type="button" onClick={removeCoupon} className="cursor-pointer inline-flex items-center gap-1 text-xs text-rose-600 hover:underline shrink-0">
                <X size={12} /> Remover
              </button>
            </div>
          )
        })()
      ) : (
        <div className="flex items-end gap-3">
          <Input label="Código do cupom" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="amplamaisparceiros" wrapperClassName="max-w-xs" clearable={false} />
          <Button type="button" onClick={applyCoupon} loading={couponLoading} iconLeft={<Ticket size={14} />} disabled={!planId || !couponCode.trim()}>
            Aplicar
          </Button>
        </div>
      )}
      {!planId && <p className="mt-2 text-xs text-gray-400">Selecione um plano antes de aplicar o cupom.</p>}
    </SectionShell>
  )

  const workflowSection = (
    <SectionShell title="Workflow" description="Envio de fatura, responsável e contrato.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller name="billingByMail" control={form.control} render={({ field }) => (
          <div className="md:col-span-2">
            <Toggle checked={field.value} onChange={field.onChange} label="Enviar fatura por correio" />
          </div>
        )} />
        {billingByMail && (
          <Controller name="seasonality" control={form.control} render={({ field, fieldState }) => (
            <Select label="Periodicidade de envio" required value={field.value ?? ''} onChange={(e) => field.onChange((e.target.value || null) as FormValues['seasonality'])}
              options={[
                { value: 'SEMESTRAL', label: 'Semestral' },
                { value: 'ANUAL',     label: 'Anual' },
              ]}
              searchable={false} error={fieldState.error?.message} />
          )} />
        )}

        <Controller name="managerId" control={form.control} render={({ field, fieldState }) => (
          <Select label="Responsável" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}
            placeholder="Selecione o responsável"
            options={managers.flatMap(g => g.users.map(u => ({ value: u.id, label: `${u.name} · ${g.type}` })))}
            error={fieldState.error?.message} wrapperClassName="md:col-span-2" />
        )} />

        <div className="md:col-span-2">
          <label className="text-xs font-medium text-gray-700 mb-1 block">Contrato (PDF)</label>
          {isEdit && id ? (
            <FileUpload
              accept="pdf"
              currentUrl={null}
              currentName={null}
              onSelect={async (file) => {
                try {
                  await SubscribersService.uploadContract(id, file)
                  toast.success({ title: 'Contrato enviado', description: 'O PDF foi anexado ao assinante.' })
                } catch {
                  toast.error({ title: 'Falha no upload', description: 'Não foi possível enviar o contrato.' })
                }
              }}
              maxMB={20}
              disabled={!canContract}
              hint="PDF até 20MB."
            />
          ) : (
            <p className="text-xs text-gray-400">Salve o assinante primeiro para anexar o contrato.</p>
          )}
        </div>
      </div>
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'basic',    label: 'Dados pessoais', icon: User,       content: basicSection,    hasError: basicErr, errorCount: basicErr ? 1 : 0 },
    { key: 'location', label: 'Localização',    icon: MapPin,      content: locationSection, hasError: addrErr,  errorCount: addrErr ? 1 : 0 },
    { key: 'plan',     label: 'Plano',          icon: Package,     content: planSection,     hasError: planErr,  errorCount: planErr ? 1 : 0 },
    ...(planAllowsDependents ? [{
      key: 'deps', label: `Dependentes (${dependents.length})`, icon: Users, content: dependentsSection,
    } as FormSection] : []),
    ...((isEdit && dependents.some(d => d.membershipCardNumber)) ? [{
      key: 'dep-cards', label: 'Carteirinhas', icon: CreditCard, content: (
        <SectionShell title="Carteirinhas dos dependentes" description="Número da carteirinha de cada dependente (somente leitura).">
          <div className="overflow-x-auto bg-white rounded-md ring-1 ring-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">Dependente</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">Carteirinha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dependents.filter(d => d.membershipCardNumber).map((d, i) => (
                  <tr key={d.id ?? i}>
                    <td className="px-3 py-2 text-gray-700">{d.name || '—'}</td>
                    <td className="px-3 py-2 font-mono text-gray-800">{d.membershipCardNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionShell>
      ),
    } as FormSection] : []),
    ...(hideBilling ? [] : [{ key: 'billing', label: 'Faturamento', icon: Receipt, content: billingSection, hasError: billErr, errorCount: billErr ? 1 : 0 } as FormSection]),
    ...((!isEdit && paymentMethod === 'CARTÃO DE CRÉDITO') ? [{
      key: 'card', label: 'Dados do cartão', icon: CreditCard, content: cardSection,
    } as FormSection] : []),
    { key: 'coupon',   label: 'Cupom de desconto', icon: Ticket,  content: couponSection },
    { key: 'workflow', label: 'Workflow',       icon: Cog,        content: workflowSection, hasError: wfErr, errorCount: wfErr ? 1 : 0 },
    { key: 'observations', label: 'Observações', icon: FileText, content: (
      <SectionShell title="Observações" description="Anotações internas sobre o associado.">
        <Textarea label="Observações" rows={4} {...form.register('observations')} placeholder="Anotações internas" />
      </SectionShell>
    ) },
    { key: 'documents', label: 'Documentos', icon: Paperclip, content: (
      <DocumentsManager
        service={standardDocuments(`/commercial/subscribers/${id}`)}
        enabled={isEdit}
        readOnly={!canEdit}
        description="Anexe e gerencie os documentos do associado."
      />
    ) },
    { key: 'info',     label: 'Informações',    icon: Network,    content: (
      <SectionShell title="Informações" description="Responsável direto, árvore de comissionamento e empresa.">
        <CommissionInfoPanel enabled={isEdit} load={() => SubscribersService.commissionInfo(id!)} />
      </SectionShell>
    ) },
  ]

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit, (e) => {
      onValidationFail(e)
      if (basicErr) setActiveSection('basic')
      else if (addrErr) setActiveSection('location')
      else if (planErr) setActiveSection('plan')
      else if (billErr) setActiveSection('billing')
      else if (wfErr) setActiveSection('workflow')
    })}>
      <PageHeader
        title={isEdit ? 'Editar assinante' : 'Novo assinante'}
        subtitle="Titulares e dependentes"
        crumbs={[{ label: 'Comercial' }, { label: 'Assinantes', to: '/commercial/subscribers' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/commercial/subscribers"
        actions={<Button type="submit" loading={form.formState.isSubmitting} iconLeft={<Save size={14} />} disabled={!canSave}
          title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} assinantes`}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </form>
  )
}

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

// =============================================================================
// DependentsManager — lista local (1 sub-form de add/edit + tabela)
// =============================================================================
const GENDERS_DEP = GENDERS

function DependentsManager({
  dependents, onChange, degrees, allowedDegreeIds, maritalStatuses, readOnly = false,
}: {
  dependents: DepRow[]
  onChange: (next: DepRow[]) => void
  degrees: DegreeRelation[]
  allowedDegreeIds: string[]
  maritalStatuses: MaritalStatus[]
  /** Sem permissão de salvar o titular: só consulta a lista. */
  readOnly?: boolean
}) {
  const [editor, setEditor] = useState<{ open: boolean; index: number | null }>({ open: false, index: null })
  const [deleting, setDeleting] = useState<number | null>(null)

  const degreeLabel = (idv: string) => degrees.find(d => d.id === idv)?.degreeRelationship ?? '—'

  function openCreate() { setEditor({ open: true, index: null }) }
  function openEdit(i: number) { setEditor({ open: true, index: i }) }
  function handleSave(row: DepRow) {
    onChange(editor.index !== null ? dependents.map((d, i) => i === editor.index ? { ...d, ...row, status: d.status, membershipCardNumber: d.membershipCardNumber } : d) : [...dependents, row])
    setEditor({ open: false, index: null })
  }

  return (
    <SectionShell title="Dependentes" description="Pessoas vinculadas ao titular. Apenas os graus de parentesco permitidos pelo plano são exibidos.">
      {!readOnly && (
        <div className="flex items-center justify-end mb-3">
          <Button type="button" size="sm" iconLeft={<Plus size={12} />} onClick={openCreate}>Adicionar dependente</Button>
        </div>
      )}

      {dependents.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">Nenhum dependente adicionado.</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-md ring-1 ring-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">Nome</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">CPF</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Parentesco</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Situação</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Nascimento</th>
                <th className="px-2 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dependents.map((d, i) => (
                <tr key={i} className={d.status === 'INATIVO' ? 'bg-gray-50/70' : undefined}>
                  <td className={`px-3 py-2 text-sm font-medium ${d.status === 'INATIVO' ? 'text-gray-400' : 'text-gray-700'}`}>{d.name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 text-center">{d.cpf || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 text-center">{degreeLabel(d.degreeRelationshipId)}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant={d.status === 'INATIVO' ? 'gray' : 'green'}>{d.status === 'INATIVO' ? 'Inativo' : 'Ativo'}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 text-center">{d.birthDate || '—'}</td>
                  <td className="px-2 py-2 text-right">
                    {!readOnly && <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(i)} aria-label="Editar"
                        className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition">
                        <Pencil size={12} />
                      </button>
                      <button type="button" onClick={() => setDeleting(i)} aria-label="Remover"
                        className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50 transition">
                        <Trash2 size={12} />
                      </button>
                    </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor.open && (
        <DependentEditor
          row={editor.index !== null ? dependents[editor.index] : null}
          degrees={degrees}
          allowedDegreeIds={allowedDegreeIds}
          maritalStatuses={maritalStatuses}
          onClose={() => setEditor({ open: false, index: null })}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => { if (deleting !== null) onChange(dependents.filter((_, i) => i !== deleting)); setDeleting(null) }}
        title="Remover dependente?"
        description="A remoção será aplicada ao salvar o assinante."
        confirmLabel="Remover"
        variant="danger"
      />
    </SectionShell>
  )
}

// Add/editar dependente via SHEET lateral (regra global do módulo).
function DependentEditor({
  row, degrees, allowedDegreeIds, maritalStatuses, onClose, onSave,
}: {
  row: DepRow | null
  degrees: DegreeRelation[]
  allowedDegreeIds: string[]
  maritalStatuses: MaritalStatus[]
  onClose: () => void
  onSave: (row: DepRow) => void
}) {
  const [draft, setDraft] = useState<DepRow>(row ?? { name: '', cpf: '', rg: '', birthDate: '', gender: 'MASCULINO', maritalStatusId: '', degreeRelationshipId: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = (p: Partial<DepRow>) => setDraft(d => ({ ...d, ...p }))

  const allowed = new Set(allowedDegreeIds)
  const degreeOptions = useMemo(
    () => degrees.filter(d => allowed.has(d.id)).map(d => ({ value: d.id, label: d.degreeRelationship })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [degrees, allowedDegreeIds],
  )

  function save() {
    const e: Record<string, string> = {}
    if (!draft.name.trim()) e.name = 'Informe o nome do dependente.'
    if (!draft.degreeRelationshipId) e.degree = 'Selecione o grau de parentesco.'
    if (!draft.cpf || !draft.cpf.trim()) e.cpf = 'Informe o CPF.'
    else if (!CPF_RE.test(onlyDigits(draft.cpf))) e.cpf = 'O CPF deve ter 11 dígitos.'
    else if (!isValidCpf(onlyDigits(draft.cpf))) e.cpf = 'CPF inválido.'
    if (!draft.birthDate || !draft.birthDate.trim()) e.birthDate = 'Informe a data de nascimento.'
    setErrors(e)
    if (Object.keys(e).length) return
    onSave(draft)
  }

  return (
    <Sheet open onClose={onClose} size="lg" title={row ? 'Editar dependente' : 'Novo dependente'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={save} iconLeft={<Save size={14} />}>Salvar</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Input label="Nome completo" required value={draft.name} onChange={(e) => { set({ name: e.target.value }); setErrors(p => ({ ...p, name: '' })) }} placeholder="Nome do dependente" error={errors.name} />
        </div>
        <Select label="Grau de parentesco" required value={draft.degreeRelationshipId} onChange={(e) => { set({ degreeRelationshipId: e.target.value }); setErrors(p => ({ ...p, degree: '' })) }}
          placeholder="Selecione" options={degreeOptions} error={errors.degree} />
        <Input label="CPF" required value={draft.cpf} onChange={(e) => { set({ cpf: maskCpf(e.target.value) }); setErrors(p => ({ ...p, cpf: '' })) }} placeholder="000.000.000-00" error={errors.cpf} />
        <Input label="RG" value={draft.rg} onChange={(e) => set({ rg: e.target.value })} placeholder="Documento de identidade" />
        <Input label="Data de nascimento" type="date" required value={draft.birthDate} onChange={(e) => { set({ birthDate: e.target.value }); setErrors(p => ({ ...p, birthDate: '' })) }} clearable={false} error={errors.birthDate} />
        <Select label="Gênero" required value={draft.gender} onChange={(e) => set({ gender: e.target.value as DepRow['gender'] })} options={GENDERS_DEP} searchable={false} clearable={false} />
        <Select label="Estado civil" value={draft.maritalStatusId} onChange={(e) => set({ maritalStatusId: e.target.value })} placeholder="Selecione" options={maritalStatuses.map(m => ({ value: m.id, label: m.description }))} />
      </div>
    </Sheet>
  )
}
